import type { UIMessage } from "@ai-sdk/react"
import { get } from "radash"

import { getSentryClient, SENTRY_TAGS } from "~utils/sentry"

const MESSAGE_ROLES = {
    ASSISTANT: "assistant",
    USER: "user",
    SYSTEM: "system",
} as const
export const MESSAGE_PART_STATES = {
    DONE: "done",
}
export const MESSAGE_PART_TYPES = {
    REASONING: "reasoning",
    TEXT: "text",
    SOURCE_URL: "source-url",
    SOURCE_DOCUMENT: "source-document",
    FILE: "file",
    STEP_START: "step-start",
    DYNAMIC_TOOL: "dynamic-tool",
} as const
// The tool type ends up "tool-TOOL_NAME". So, we need to check for "tool"
// as the start of the type instead of being able to use a strict match.
export const TOOL_PART_TYPE_PREFIX = "tool"
const HANDLED_PART_TYPES = new Set<string>([
    MESSAGE_PART_TYPES.TEXT,
    MESSAGE_PART_TYPES.REASONING,
    MESSAGE_PART_TYPES.SOURCE_URL,
    MESSAGE_PART_TYPES.SOURCE_DOCUMENT,
    MESSAGE_PART_TYPES.FILE,
    MESSAGE_PART_TYPES.STEP_START,
    MESSAGE_PART_TYPES.DYNAMIC_TOOL,
])
const UNHANDLED_CHAT_PART_ERROR_MESSAGE = "Unhandled AI chat part"
const AI_CHAT_SENTRY_SOURCE = "ai-chat"
const AI_CHAT_PART_TYPE_TAG = "ai_chat_part_type"

export function getAiSuggestionIdFromMessage(message: UIMessage) {
    const lastToolCall = message.parts.findLast((part) =>
        part.type.startsWith(TOOL_PART_TYPE_PREFIX)
    )

    return get<string | undefined>(lastToolCall, "output.ai_suggestion_id")
}

export function isMessageFromAi(message: UIMessage) {
    return message.role === MESSAGE_ROLES.ASSISTANT
}

// Returns true for part types the chat UI renders explicitly. Tool parts
// (both static `tool-<NAME>` and `dynamic-tool`) are routed to
// `ChatMessageToolCall`. Anything else is considered unhandled and
// surfaced for visibility (debug panel + Sentry). Keep this list in sync
// with the switch in `ChatMessagePart`.
export function isHandledChatPart(part: ChatPart) {
    if (HANDLED_PART_TYPES.has(part.type)) return true
    return part.type.startsWith(TOOL_PART_TYPE_PREFIX)
}

export function reportUnknownChatPart(part: ChatPart) {
    const sentry = getSentryClient()
    sentry.withScope((scope) => {
        scope.setTag(SENTRY_TAGS.INTEGRATION, AI_CHAT_SENTRY_SOURCE)
        scope.setTag(AI_CHAT_PART_TYPE_TAG, part.type)
        scope.setExtra?.("part", part)
        sentry.captureException(new Error(UNHANDLED_CHAT_PART_ERROR_MESSAGE))
    })
}

type ChatPart = UIMessage["parts"][number]
